import {
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AudioCell from "../component/AudioCell";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useEffect, useState } from "react";
import { apiService } from "../app/api";
import { handleAxiosError } from "../helper/errorHandler";
import { toast } from "react-toastify";
import BaseLayout from "../layout/BaseLayout";
import KeyboardBackspaceIcon from "@mui/icons-material/KeyboardBackspace";
import { useNavigate, useParams } from "react-router-dom";
import { retrieveFromLocal } from "../helper/localStore";

const InstantClonedVoice = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loader, setLoader] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);
  const [voices, setVoices] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const user = retrieveFromLocal("user");

  useEffect(() => {
    fetchClonedVoices(page, search);
  }, [page, search]);

  const fetchClonedVoices = async (pageNum: number, searchQuery: string) => {
    try {
      setLoader(true);
      const { data } = await apiService.get(
        `/instant-voice-cloning?page=${pageNum}&limit=10&user_id=${id}&search=${encodeURIComponent(
          searchQuery
        )}`
      );
      setVoices(data.data || []);
      setTotalPages(data.total_pages || 1);
    } catch (error: any) {
      handleAxiosError(error);
    } finally {
      setLoader(false);
    }
  };

  const handleDeleteClick = (id: string) => {
    setSelectedVoiceId(id);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedVoiceId) return;
    try {
      await apiService.delete(`/instant-voice-cloning/${selectedVoiceId}`);
      setVoices((prev) => prev.filter((v) => v._id !== selectedVoiceId));
      toast.success("Voice deleted successfully");
      fetchClonedVoices(1, search);
    } catch (error: any) {
      handleAxiosError(error);
    } finally {
      setDeleteDialogOpen(false);
      setSelectedVoiceId(null);
    }
  };

  return (
    <BaseLayout>
      <Box
        display={"flex"}
        justifyContent={"space-between"}
        alignItems={"center"}
        pb={2}
      >
        <Box display={"flex"} alignItems={"center"}>
          {!user && (
            <IconButton onClick={() => navigate(-1)}>
              <KeyboardBackspaceIcon />
            </IconButton>
          )}
          <Typography
            fontSize={30}
            fontWeight={700}
            textTransform={"capitalize"}
          >
            Instant Cloned Voice By{" "}
            {sessionStorage.getItem("user_name") || "User"}
          </Typography>
        </Box>
      </Box>
      <Box flex={1}>
        <Box display="flex" alignItems="center" justifyContent={"end"} mb={2}>
          <TextField
            size="small"
            placeholder="Search for speaker..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 5,
              },
              width: "300px",
            }}
          />
        </Box>

        {loader ? (
          <Box display="flex" justifyContent="center" mt={5}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              pr: 1,
              mb: 2,
            }}
          >
            {voices.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No cloned voices yet. Start by cloning one!
              </Typography>
            ) : (
              voices.map((voice) => (
                <Card
                  key={voice._id}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "#f9fafb",
                    m: 2,
                  }}
                >
                  {/* Header: Speaker + Status */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    <Box>
                      <Typography fontWeight="600" fontSize={16}>
                        Speaker: {voice.speaker}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {voice.src_lang} → {voice.tgt_lang}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      {/* Delete button */}
                      <Tooltip title="Delete Voice">
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(voice._id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>

                  <Box display={"flex"} justifyContent={"space-between"}>
                    {/* Reference Audio */}
                    {voice.ref_audio_url && (
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Reference Audio
                        </Typography>
                        <AudioCell link={voice.ref_audio_url} />
                      </Box>
                    )}

                    {/* Generated Audio */}
                    {voice.output_file && (
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mb: 0.5, display: "block" }}
                        >
                          Generated Audio
                        </Typography>
                        <AudioCell link={voice.output_file} />
                      </Box>
                    )}
                  </Box>

                  {/* Generated Text */}
                  <Box
                    width={"100%"}
                    display={"flex"}
                    justifyContent={"space-between"}
                    alignItems={"center"}
                  >
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        mt: 1,
                        bgcolor: "#f3f4f6",
                        p: 1,
                        borderRadius: 1,
                        width: "80%",
                      }}
                    >
                      {voice.gen_text}
                    </Typography>
                    <Tooltip title="Copy text">
                      <IconButton
                        onClick={() =>
                          navigator.clipboard.writeText(voice.gen_text)
                        }
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Card>
              ))
            )}
          </Box>
        )}

        {/* Pagination */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Button
            size="small"
            disabled={page <= 1}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
          >
            Prev
          </Button>
          <Typography variant="body2" sx={{ px: 2 }}>
            Page {page} of {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page >= totalPages}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
          >
            Next
          </Button>
        </Box>

        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this cloned voice?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </BaseLayout>
  );
};
export default InstantClonedVoice;
